import {
  FileInput,
  Geogroup,
  GeogroupNewsPost,
  GeogroupService,
  LinkIcon,
  TFile,
} from '@geovelo-frontends/commons';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormikHelpers, useFormik } from 'formik';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { FormEvent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import Button from '../../components/button';
import Dialog from '../../components/dialog';
import { AppContext } from '../../context';
import environment from '../../environment';

interface IValues {
  text: string;
  linkUrl: string;
  linkLabel: string;
  customPublicationDate: boolean;
  publicationDate: string;
}

type TProps = Omit<DialogProps, 'onClose'> & {
  geogroup?: Geogroup;
  post?: GeogroupNewsPost;
  onClose: (post?: GeogroupNewsPost) => void;
};

function NewsFormDialog({ geogroup, post, open, onClose, ...props }: TProps): JSX.Element {
  const [photo, setPhoto] = useState<TFile>();
  const [confirmMessageShowed, showConfirmMessage] = useState(false);
  const [, setNow] = useState(moment());
  const {
    user: { current: currentUser },
  } = useContext(AppContext);
  const { t } = useTranslation();
  const {
    transitions: { duration },
  } = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { isSubmitting, values, errors, setValues, setErrors, handleChange, handleSubmit } =
    useFormik<IValues>({
      initialValues: {
        text: '',
        linkUrl: '',
        linkLabel: '',
        customPublicationDate: false,
        publicationDate: '',
      },
      validationSchema: Yup.object().shape({
        text: Yup.string().required(),
        linkUrl: Yup.string().url(),
      }),
      onSubmit,
      enableReinitialize: true,
    });

  useEffect(() => {
    if (open) {
      const _now = moment();
      setNow(_now);

      if (post) {
        const { text, link, photo, publicationDate } = post;
        const customPublicationDate = publicationDate?.isAfter(_now) || false;

        setValues({
          text,
          linkUrl: link?.url || '',
          linkLabel: link?.label || '',
          customPublicationDate,
          publicationDate: customPublicationDate ? publicationDate.format('YYYY-MM-DDTHH:mm') : '',
        });
        // setInitialText(text);
        setPhoto(
          photo
            ? {
                url: `${environment.backendUrl}${photo}`,
                name: photo.slice(photo.lastIndexOf('/') + 1),
              }
            : undefined,
        );
      } else {
        setValues({
          text: '',
          linkUrl: '',
          linkLabel: '',
          customPublicationDate: false,
          publicationDate: '',
        });
        // setInitialText('');
        setPhoto(undefined);
      }
    }
  }, [open]);

  async function onSubmit(
    {
      text,
      linkUrl: _linkUrl,
      linkLabel: _linkLabel,
      customPublicationDate,
      publicationDate,
    }: IValues,
    { setSubmitting }: FormikHelpers<IValues>,
  ) {
    if (!currentUser || !geogroup) return;

    setSubmitting(true);

    let linkUrl: string | undefined;
    let linkLabel: string | undefined;
    if (_linkLabel && !_linkUrl) return;

    if (_linkUrl) linkUrl = _linkUrl;
    if (_linkLabel) linkLabel = _linkLabel;

    try {
      if (post) {
        const updatedNews = await GeogroupService.updateNewsPost(geogroup.id, post.id, {
          text,
          link_url: linkUrl,
          link_label: linkLabel,
          photo: photo instanceof File || photo === null ? photo : undefined,
          ...(customPublicationDate ? { publication_date: publicationDate } : {}),
        });

        onClose(updatedNews);
      } else {
        const createdNews = await GeogroupService.addNewsPost(
          geogroup.id,
          {
            text,
            link_url: linkUrl,
            link_label: linkLabel,
            photo: photo instanceof File ? photo : undefined,
            publication_date: customPublicationDate
              ? publicationDate
              : moment().format('YYYY-MM-DDTHH:mm'),
            geo_group: geogroup.id || 0,
          },
          undefined,
          true,
        );

        onClose(createdNews);
      }

      setTimeout(() => showConfirmMessage(false), duration.leavingScreen);
    } catch (err) {
      enqueueSnackbar(
        t('geovelo.communities.news.form.not', { context: post ? 'updated' : 'created' }),
        { variant: 'error' },
      );
      setSubmitting(false);
    }
  }

  function handleConfirm(event?: FormEvent<HTMLFormElement>) {
    event?.preventDefault();

    if (values.linkLabel && !values.linkUrl) {
      setErrors({ linkUrl: 'error' });

      return;
    }

    showConfirmMessage(true);
  }

  return (
    <Dialog disableEscapeKeyDown fullWidth maxWidth="sm" open={open} scroll="paper" {...props}>
      <DialogTitle>
        {t('geovelo.communities.news.form.title', { context: post ? 'update' : undefined })}
      </DialogTitle>
      <DialogContent>
        {confirmMessageShowed ? (
          <Typography>
            {t('geovelo.communities.news.form.confirm', { context: post ? 'update' : undefined })}
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
            <TextField
              fullWidth
              multiline
              required
              disabled={isSubmitting}
              error={Boolean(errors.text)}
              id="text"
              InputLabelProps={{ shrink: true }}
              label={t('geovelo.communities.news.form.text')}
              margin="none"
              name="text"
              onChange={handleChange}
              rows={3}
              value={values.text}
              variant="outlined"
            />
            <TextField
              fullWidth
              disabled={isSubmitting}
              error={Boolean(errors.linkUrl)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              label={t('geovelo.communities.news.form.link_url')}
              margin="dense"
              name="linkUrl"
              onChange={handleChange}
              size="small"
              type="url"
              value={values.linkUrl}
              variant="outlined"
            />
            <TextField
              fullWidth
              disabled={isSubmitting}
              InputLabelProps={{ shrink: true }}
              label={t('geovelo.communities.news.form.link_label')}
              margin="dense"
              name="linkLabel"
              onChange={handleChange}
              size="small"
              value={values.linkLabel}
              variant="outlined"
            />
            <FileInput file={photo} onChange={setPhoto} size="small" type="image" />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          disabled={isSubmitting}
          onClick={confirmMessageShowed ? () => showConfirmMessage(false) : () => onClose()}
        >
          {t(`commons.actions.${confirmMessageShowed ? 'cancel' : 'close'}`)}
        </Button>
        <Button
          color="primary"
          disabled={isSubmitting}
          onClick={() => {
            confirmMessageShowed ? handleSubmit() : handleConfirm();
          }}
          variant="contained"
        >
          {post ? t('commons.actions.update') : t('geovelo.communities.actions.post')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NewsFormDialog;
